import {
  createMemoryHistory,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  type RouterHistory,
} from "@tanstack/react-router";
import { lazy } from "react";
import { FormattedMessage } from "react-intl";
import { ForgotPasswordPage } from "../features/access/containers/forgot-password-page.tsx";
import { InvitationPage } from "../features/access/containers/invitation-page.tsx";
import { LoginPage } from "../features/access/containers/login-page.tsx";
import { createDeviceBlocksMapRoutes } from "./device-blocks-map-routes.tsx";
import { AppShell } from "../features/shell/containers/app-shell.tsx";
import { createInvestigationRoutes } from "./investigation-routes.tsx";
import { appMessages } from "../shared/i18n/messages.ts";
import { LEGACY_ENVIRONMENT_PARAM } from "../shared/installation.ts";
import { InstallationGate, LEGACY_LINK_STATE_KEY } from "../shared/installation-gate.tsx";
import { isReleaseFlagOn } from "../shared/release-flags.ts";
import { DefaultRouteError } from "../shared/route-error.tsx";
import { parseSearch, stringifySearch } from "../shared/url-search.ts";

// Public screens (login, invitation) sit outside the shell; everything else is the signed-in app.
// The root waits for nothing but starts `GET /installation` first, and every screen, the public
// ones included, shows the installation's environment (design D7).
const rootRoute = createRootRoute({
  component: function Root() {
    return (
      <InstallationGate>
        <Outlet />
      </InstallationGate>
    );
  },
  notFoundComponent: () => (
    <main id="main-content">
      <h1>
        <FormattedMessage {...appMessages.notFoundTitle} />
      </h1>
    </main>
  ),
});

function withoutLegacyEnvironment(search: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(search).filter(([key]) => key !== LEGACY_ENVIRONMENT_PARAM),
  );
}

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  // No screen takes an environment (spec explorer-access, "[FR] Installation environment badge"):
  // an old link that still carries `?environment=` is redirected (replace) to the same screen
  // without it; the value rides along in the history state so the shell can say once that the
  // link was made for the other installation.
  validateSearch: (search: Record<string, unknown>) => withoutLegacyEnvironment(search),
  beforeLoad: ({ location }) => {
    const search: Record<string, unknown> = location.search;
    if (LEGACY_ENVIRONMENT_PARAM in search) {
      const linked = search[LEGACY_ENVIRONMENT_PARAM];
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirects are thrown
      throw redirect({
        to: location.pathname,
        search: withoutLegacyEnvironment(search),
        state: (previous) => ({ ...previous, [LEGACY_LINK_STATE_KEY]: linked }),
        replace: true,
      });
    }
  },
  component: ShellLayout,
});

const PlatformOperatorDetail = lazy(() =>
  import("../features/platform/containers/operator-detail-page.tsx").then((m) => ({
    default: m.OperatorDetailPage,
  })),
);

function ShellLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

// Home, transactions by IP, user and device list, user profile (tasks 16.1-16.4).
const investigationRoutes = createInvestigationRoutes(appRoute);

const consoleUsersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/console-users",
  component: lazyRouteComponent(
    () => import("../features/access/containers/console-users-page.tsx"),
    "ConsoleUsersPage",
  ),
});
const identityProviderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/identity-provider",
  component: lazyRouteComponent(
    () => import("../features/access/containers/identity-provider-page.tsx"),
    "IdentityProviderPage",
  ),
});

// Settings of the signed-in user (change add-console-account-settings, design D1): every console
// user, no permission; `#profile`, `#security` and `#preferences` link to its sections.
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: lazyRouteComponent(
    () => import("../features/settings/containers/settings-page.tsx"),
    "SettingsPage",
  ),
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit",
  component: lazyRouteComponent(
    () => import("../features/audit/containers/audit-page.tsx"),
    "AuditPage",
  ),
});

// The customer's "Integration" screen (adopt-installation-per-customer design D14 item 4): an
// Administrator configures its operators and issues their API credentials, only in a customer
// installation (the page checks GET /installation and the permissions; the API is the authority).
const integrationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/integration",
  component: lazyRouteComponent(
    () => import("../features/integration/containers/integration-page.tsx"),
    "IntegrationPage",
  ),
});

// Platform area: shown only to Platform Administrators; every screen also checks permission and
// treats a 404 from the API as "not available".
const platformRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/platform",
  beforeLoad: ({ location }) => {
    if (location.pathname.replace(/\/$/, "") === "/platform") {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirects are thrown
      throw redirect({ to: "/platform/organizations" });
    }
  },
  component: Outlet,
});
const platformOperatorsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: "/operators",
  component: lazyRouteComponent(
    () => import("../features/platform/containers/operators-page.tsx"),
    "OperatorsPage",
  ),
});
const platformOperatorRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: "/operators/$operatorId",
  component: function PlatformOperatorRoute() {
    const { operatorId } = platformOperatorRoute.useParams();
    return <PlatformOperatorDetail operatorId={operatorId} />;
  },
});
const platformOrganizationsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: "/organizations",
  component: lazyRouteComponent(
    () => import("../features/platform/containers/organizations-page.tsx"),
    "OrganizationsPage",
  ),
});

const platformAdministratorsRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: "/administrators",
  component: lazyRouteComponent(
    () => import("../features/platform/containers/platform-administrators-page.tsx"),
    "PlatformAdministratorsPage",
  ),
});

// The Fleet tab (add-control-plane-console design D1): routed only where the release flag
// fleet.console is on (the platform installation's build).
const platformFleetRoute = createRoute({
  getParentRoute: () => platformRoute,
  path: "/fleet",
  component: lazyRouteComponent(
    () => import("../features/fleet/containers/fleet-page.tsx"),
    "FleetPage",
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  // `error` comes from the SSO callback redirect and is user-controlled: only read, never trusted.
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.error === "string" ? { error: search.error } : {}),
    ...(typeof search.reason === "string" ? { reason: search.reason } : {}),
  }),
  component: function LoginRoute() {
    const search = loginRoute.useSearch();
    return <LoginPage redirectError={search.error} reason={search.reason} />;
  },
});
const invitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invitations/$token",
  component: function InvitationRoute() {
    const { token } = invitationRoute.useParams();
    return <InvitationPage token={token} />;
  },
});

// Self-service password reset (add-console-account-settings D14): public; routed only while the release flag iam.password-reset is on in this build.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

// Screen routes load lazily with `lazyRouteComponent`; the placeholders go as their tasks land.
const routeTree = rootRoute.addChildren([
  appRoute.addChildren([
    ...investigationRoutes,
    ...createDeviceBlocksMapRoutes(appRoute),
    consoleUsersRoute,
    identityProviderRoute,
    auditRoute,
    integrationRoute,
    settingsRoute,
    platformRoute.addChildren([
      platformOperatorsRoute,
      platformOperatorRoute,
      platformOrganizationsRoute,
      platformAdministratorsRoute,
      ...(isReleaseFlagOn("fleet.console") ? [platformFleetRoute] : []),
    ]),
  ]),
  loginRoute,
  invitationRoute,
  ...(isReleaseFlagOn("iam.password-reset") ? [forgotPasswordRoute] : []),
]);

// Any route that fails to load or render (a stale chunk after a deploy included) shows the
// Explorer's error screen, never the router's bare default.
export function createAppRouter(history?: RouterHistory) {
  return createRouter({
    routeTree,
    parseSearch,
    stringifySearch,
    defaultErrorComponent: DefaultRouteError,
    ...(history ? { history } : {}),
  });
}

export function createTestRouter(initialPath = "/") {
  return createAppRouter(createMemoryHistory({ initialEntries: [initialPath] }));
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
